import {
  ChakraBox,
  ChakraFlex,
  ChakraCollapse,
  Text,
  FAIconName,
  TagDropdown,
  TagProps,
  Icon,
} from '@equip.health/ui';
import {
  CarePlanGoalResponse,
  CarePlanProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import {
  CarePlanGoalStatus,
  isTerminalGoalStatus,
} from '~/lib/types/carePlan/CarePlanGoalStatus';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import {
  buildGoalStatusTagDropdownProps,
  getCarePlanGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { CarePlanTreatmentGoalExpandedView } from './CarePlanTreatmentGoalExpandedView';
import { CarePlanGoalCollapsedView } from './CarePlanGoalCollapsedView';
import { CarePlanGoalEditMenu } from './CarePlanGoalEditMenu';

export type CarePlanGoal = {
  id: string;
  title: string;
  description: string;
  status: CarePlanGoalStatus;
  statusLabel: string;
  problemId: string;
  problemType?: ProblemTypeCode;
  problem: CarePlanProblemResponse;
  latestNote?: {
    text: string;
    timestamp: string;
    providerId?: string;
  };
};

type Props = {
  goal: CarePlanGoal;
  isExpanded: boolean;
  carePlanId: string;
  treatmentEpisodeId: string;
  statusDropdownOptions?: TagProps[];
  displayProblemLabels?: boolean;
  onToggle: (goalId: string) => void;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
  onEditGoalClick: (goal: CarePlanGoalResponse) => void;
  onAddInterventionClick?: (goal: CarePlanGoal) => void;
  onRemoveGoalClick: (problemId: string) => void;
  onStatusChange: (
    goalId: string,
    statusCode: CarePlanGoalStatus,
    description: string,
  ) => void;
};

export const CarePlanGoalRow = ({
  goal,
  isExpanded,
  carePlanId,
  treatmentEpisodeId,
  statusDropdownOptions,
  displayProblemLabels = true,
  onToggle,
  onEditProblemClick,
  onEditGoalClick,
  onAddInterventionClick,
  onRemoveGoalClick,
  onStatusChange,
}: Props) => {
  const { carePlanM4MedPsychProblems: isMedPsychProblemsEnabled } =
    useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  const resolvedStatusDropdownOptions =
    statusDropdownOptions ??
    getCarePlanGoalTagDropdownOptions(isMedPsychProblemsEnabled);

  const handleToggleClick = () => {
    if (isExpanded) {
      trackEvent(CarePlanEvents.clickOnCollapseGoalChevron);
    } else {
      trackEvent(CarePlanEvents.clickOnExpandGoalChevron);
    }
    onToggle(goal.id);
  };

  const handleEditClick = () => {
    trackEvent(CarePlanEvents.clickOnEditGoalKebabMenuOption);
    onEditGoalClick(goal.problem.goals[0]);
  };

  const handleAddInterventionClick =
    onAddInterventionClick && !isTerminalGoalStatus(goal.status)
      ? () => {
          trackEvent(CarePlanEvents.clickOnAddInterventionKebabMenuOption);
          onAddInterventionClick(goal);
        }
      : undefined;

  const handleRemoveClick = () => {
    trackEvent(CarePlanEvents.clickOnRemoveGoalKebabMenuOption);
    onRemoveGoalClick(goal.problemId);
  };

  return (
    <ChakraBox
      key={goal.id}
      borderWidth="1px"
      borderColor="neutral.line"
      borderRadius="12px"
      marginBottom="12px"
      bg="white"
    >
      <ChakraBox
        as="button"
        paddingTop="18px"
        paddingLeft="18px"
        paddingRight="0px"
        paddingBottom="12px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleToggleClick}
        _hover={{ bg: 'neutral.background' }}
        width="100%"
        textAlign="left"
      >
        <ChakraFlex alignItems="center" gap="8px" flex="1">
          <Icon
            aria-label={`Expand or collapse ${goal.title}`}
            color="neutral.primary"
            name={
              isExpanded
                ? FAIconName.chevronUpRegular
                : FAIconName.chevronDownRegular
            }
          />
          <Text variant="bodyMedium">{goal.title}</Text>
        </ChakraFlex>

        <ChakraFlex
          alignItems="center"
          gap="12px"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <TagDropdown
            id={`treatment-goal-${carePlanId}-${goal.id}-status-dropdown`}
            menuOptions={resolvedStatusDropdownOptions}
            onDropdownOpen={() =>
              trackEvent(CarePlanEvents.clickOnGoalStatusMenu)
            }
            onOptionClick={(newStatus) => {
              trackEvent(CarePlanEvents.clickOnGoalStatusMenuOption);
              onStatusChange(
                goal.id,
                newStatus as CarePlanGoalStatus,
                goal.description,
              );
            }}
            {...buildGoalStatusTagDropdownProps(
              goal.status,
              goal.statusLabel,
              resolvedStatusDropdownOptions,
            )}
          />

          <CarePlanGoalEditMenu
            goalTitle={goal.title}
            onAddInterventionClick={handleAddInterventionClick}
            onEditClick={handleEditClick}
            onRemoveClick={handleRemoveClick}
          />
        </ChakraFlex>
      </ChakraBox>

      <ChakraBox
        id={`care-plan-treatment-goal-${goal.id}`}
        paddingLeft="42px"
        paddingRight="18px"
      >
        {!isExpanded && goal.latestNote && goal.latestNote.text?.trim() && (
          <CarePlanGoalCollapsedView
            noteText={goal.latestNote.text}
            timestamp={goal.latestNote.timestamp}
            providerId={goal.latestNote.providerId}
            goalId={goal.id}
          />
        )}
        <ChakraCollapse in={isExpanded} animateOpacity>
          <CarePlanTreatmentGoalExpandedView
            problem={goal.problem}
            treatmentEpisodeId={treatmentEpisodeId}
            displayProblemLabels={displayProblemLabels}
            onEditProblemClick={onEditProblemClick}
          />
        </ChakraCollapse>
      </ChakraBox>
    </ChakraBox>
  );
};
